/**
 * =============================================================================
 * SPEQ55 RFP Pro - RFP Create Page
 * =============================================================================
 *
 * Page for creating a new RFP. Wraps the RFPForm component with
 * create-specific logic.
 *
 * @module pages/rfps/RFPCreate
 * @author SPEQ55
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { post } from '../../api/client';
import RFPForm from './RFPForm';

function RFPCreate() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Handle form submission.
   */
  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await post('/rfps', formData);
      // Navigate to the new RFP's detail page
      navigate(`/app/rfps/${response.data.rfp.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create RFP. Please try again.');
      setIsSubmitting(false);
    }
  };

  /**
   * Handle cancel.
   */
  const handleCancel = () => {
    navigate('/app/rfps');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Link to="/app/rfps" className="text-accent-500 hover:text-accent-700">
            RFPs
          </Link>
          <span className="text-accent-400">/</span>
          <span className="text-accent-800">New RFP</span>
        </div>
        <h1 className="text-2xl font-bold text-accent-800">Create New RFP</h1>
        <p className="mt-1 text-accent-500">Enter the details for the new request for proposal.</p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-error-light border-l-4 border-error p-4 rounded">
          <p className="text-error-dark">{error}</p>
        </div>
      )}

      {/* Form */}
      <RFPForm
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
        submitLabel="Create RFP"
      />
    </div>
  );
}

export default RFPCreate;
